<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <div class="card">
        <div class="card-header">
          <i class="icon-reload"></i> Auditlog
        </div>
        <div class="card-body">
          <div class="audit-filter-bar">
            <div class="audit-filter-toggles">
              <label class="audit-filter-toggle">
                <input v-model="displayImportant" type="checkbox">
                <span>Important entries</span>
              </label>
              <label class="audit-filter-toggle">
                <input v-model="displayReason" type="checkbox">
                <span>Missing reasons</span>
              </label>
              <label class="audit-filter-toggle">
                <input v-model="includeTestOnly" type="checkbox">
                <span>Include Test-only</span>
              </label>
              <label v-if="canUseModerationMode" class="audit-filter-toggle">
                <input v-model="groupsEnabled" type="checkbox">
                <span>Moderation Mode</span>
              </label>
            </div>

            <div class="audit-filter-field audit-filter-staff">
              <label>Filter by Staff</label>
              <Autocomplete v-model="filterbyStaff" />
            </div>

            <div class="audit-filter-field">
              <label>Search</label>
              <input
                v-model="filterQuery"
                type="text"
                class="form-control"
                placeholder="Reason, entry, group or field...">
            </div>

            <div class="audit-filter-field">
              <label>Filter by Action</label>
              <select id="basicSelect" class="form-control" v-model="filterbyAction">
                <option value="">All</option>
                <template v-for="group in actionGroups">
                  <option :key="group.label" disabled class="audit-action-category">{{ group.label }}</option>
                  <option
                    v-for="action in group.options"
                    :key="group.label + '-' + action.value"
                    :value="action.value">
                    {{ action.label }}
                  </option>
                </template>
              </select>
            </div>
          </div>

          <b-overlay
            :show="logLoading"
            variant="dark"
            opacity="0.55"
            blur="1px"
            rounded="sm">
            <template #overlay>
              <div class="text-center text-light">
                <b-spinner variant="info"></b-spinner>
                <div class="mt-2">Loading audit log...</div>
              </div>
            </template>
            <div :class="{ 'audit-log-loading-surface': logLoading && auditlog.length === 0 }">
              <AuditLogList
                :entries="auditlog"
                :groups="groups"
                :groups-enabled="moderationModeEnabled"
                :search-query="filterQuery"
                :editable-evidence="canDeleteAuditLog"
                :allow-delete-entries="canDeleteAuditLog"
                @evidence-changed="fetchLog(false)">
              </AuditLogList>
            </div>
          </b-overlay>

          <br>
          <b-pagination size="md" :total-rows="count" v-model="page" v-on:change="fetchData" :per-page="100"></b-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import Autocomplete from './Permissions/UsernameSearch.vue'
import AuditLogList from '../../components/AuditLog/AuditLogList.vue'
import { buildAuditLogActionGroups } from '../../auditlog_permissions'

const groupsStorageKey = 'modtools_auditlog_groups_enabled'
const importantStorageKey = 'modtools_auditlog_important_enabled'

function storedBoolean (key, fallback) {
  const value = localStorage.getItem(key)
  if (value === null) {
    return fallback
  }
  return value === 'true'
}

export default {
  name: 'Auditlog',
  computed: {
    ...mapGetters({
      auditlog: 'Auditlog',
      groups: 'AuditlogGroups',
      groupRevision: 'AuditlogGroupRevision',
      logLoading: 'AuditlogLoading',
      count: 'LogCount'
    }),
    actionGroups () {
      return buildAuditLogActionGroups(permission => this.hasPermission(permission))
    },
    canUseModerationMode () {
      return this.hasPermission('ModerationMode')
    },
    moderationModeEnabled () {
      return this.canUseModerationMode && this.groupsEnabled
    },
    canDeleteAuditLog () {
      return this.hasPermission('AuditLogDelete')
    }
  },
  data () {
    return {
      page: 1,
      filterbyStaff: null,
      filterQuery: '',
      filterTimer: null,
      displayImportant: storedBoolean(importantStorageKey, true),
      displayReason: false,
      includeTestOnly: false,
      groupsEnabled: storedBoolean(groupsStorageKey, false),
      filterbyAction: ''
    }
  },
  created () {
    this.clearAuditlog()
  },
  components: {
    Autocomplete,
    AuditLogList
  },
  mounted () {
    this.fetchLog(true)
  },
  watch: {
    displayImportant () {
      localStorage.setItem(importantStorageKey, String(this.displayImportant))
      this.updateLog()
    },
    displayReason () {
      this.updateLog()
    },
    includeTestOnly () {
      this.updateLog()
    },
    groupsEnabled (enabled) {
      localStorage.setItem(groupsStorageKey, String(this.canUseModerationMode && enabled))
      this.updateLog()
    },
    filterbyAction () {
      this.updateLog()
    },
    filterbyStaff () {
      this.updateLog()
    },
    filterQuery () {
      this.scheduleUpdate()
    },
    groupRevision () {
      this.fetchLog(false)
    }
  },
  beforeDestroy () {
    window.clearTimeout(this.filterTimer)
  },
  methods: {
    ...mapActions(['clearAuditlog', 'startAuditlogLoading']),
    updateLog () {
      this.page = 1
      this.fetchLog(true)
    },
    scheduleUpdate () {
      window.clearTimeout(this.filterTimer)
      this.filterTimer = window.setTimeout(() => this.updateLog(), 250)
    },
    fetchData (e) {
      this.page = e
      this.scrollToPageTop()
      this.fetchLog(false)
    },
    scrollToPageTop () {
      this.$nextTick(() => {
        let element = this.$el
        while (element) {
          if (element.scrollHeight > element.clientHeight) {
            element.scrollTop = 0
          }
          element = element.parentElement
        }
        if (window.scrollTo) {
          window.scrollTo(0, 0)
        }
      })
    },
    fetchLog (count) {
      this.startAuditlogLoading()
      const staff = this.filterbyStaff && this.filterbyStaff.name ? this.filterbyStaff.name : ''
      this.emit('getAuditlog', {
        page: this.page,
        count: count,
        display_only_important: this.displayImportant,
        display_only_reasons: this.displayReason,
        include_test_only: this.includeTestOnly,
        filterbyAction: this.filterbyAction,
        filter_by_user: staff,
        filter_by_staff: staff,
        filter_by_search: this.filterQuery.trim(),
        moderation_mode: this.moderationModeEnabled
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.audit-log-loading-surface {
  min-height: 180px;
}

.audit-filter-bar {
  display: grid;
  grid-template-columns: minmax(220px, 1.2fr) minmax(180px, 1fr) minmax(240px, 1.4fr) minmax(220px, 1.2fr);
  gap: 14px;
  align-items: end;
  padding: 14px;
  margin-bottom: 18px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.audit-filter-toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.audit-filter-toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  margin: 0;
  padding: 7px 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.16);
  font-size: 0.82rem;
}

.audit-filter-toggle input {
  margin: 0;
}

.audit-filter-field label {
  display: block;
  margin-bottom: 5px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.audit-action-category {
  color: #8f9ba6;
  background: #1b2229;
}

@media (max-width: 1199px) {
  .audit-filter-bar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .audit-filter-bar {
    grid-template-columns: 1fr;
  }

}
</style>
